// 📄 components/Configuracion/ConfiguracionProgramasZ.tsx
// Gestión de los Programas Z (desarrollos SAP personalizados) propios de
// ESTE proyecto — mismo patrón estructural que ConfiguracionBadisZ.tsx
// (lista + modal alta/edición + ConfirmDialog para borrar), guardado
// DIRECTO contra Firestore (ver programaZService.ts).
import { useEffect, useState } from "react";
import { Code2, Plus, RefreshCw, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext.tsx";
import { getProgramasZ, eliminarProgramaZ, ProgramaZ } from "../../services/programaZService.ts";
import { solucionBadiMeta, getBadis, BadiObjeto } from "../../services/catalogoBadisService.ts";
import { getTransacciones, TransaccionSap } from "../../services/catalogoTransaccionesService.ts";
import ConfirmDialog from "../ui/ConfirmDialog.tsx";
import Alert from "../ui/Alert.tsx";
import ProgramaZFormModal from "./ProgramaZFormModal.tsx";

interface ConfiguracionProgramasZProps {
  projectId: string;
  projectNombre?: string;
}

export default function ConfiguracionProgramasZ({ projectId, projectNombre }: ConfiguracionProgramasZProps) {
  const { profile } = useAuth();
  const esAdmin = profile?.role === "admin";
  const [programas, setProgramas] = useState<ProgramaZ[]>([]);
  const [badis, setBadis] = useState<BadiObjeto[]>([]);
  const [transacciones, setTransacciones] = useState<TransaccionSap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<ProgramaZ | null>(null);
  const [aEliminar, setAEliminar] = useState<ProgramaZ | null>(null);
  const [eliminando, setEliminando] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const showAlert = (msg: string, type: "success" | "error" | "warning" = "success") => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 4000);
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const [data, b, t] = await Promise.all([getProgramasZ(projectId), getBadis(), getTransacciones()]);
      setProgramas(data);
      setBadis(b);
      setTransacciones(t);
    } catch (err) {
      console.error("Error cargando los Programas Z:", err);
      setError("No se pudieron cargar los Programas Z de este proyecto.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const badiNombrePorId = new Map(badis.map((b) => [b.id, b.nombre]));
  const transaccionNombrePorId = new Map(transacciones.map((t) => [t.id, t.transaccion]));

  const handleGuardado = () => {
    const eraEdicion = !!editando;
    setModalOpen(false);
    setEditando(null);
    cargar();
    showAlert(eraEdicion ? "Programa Z actualizado correctamente." : "Programa Z guardado correctamente.");
  };

  const handleCancelarModal = () => {
    setModalOpen(false);
    setEditando(null);
  };

  const handleConfirmarEliminar = async () => {
    if (!aEliminar || eliminando) return;
    setEliminando(true);
    try {
      await eliminarProgramaZ(projectId, aEliminar.id);
      setAEliminar(null);
      cargar();
      showAlert("Programa Z eliminado correctamente.");
    } catch (err: any) {
      showAlert(err.message || "Error al eliminar el Programa Z.", "error");
    } finally {
      setEliminando(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <p className="text-sm">Cargando Programas Z...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Alert message={alertMessage} type={alertType} onClose={() => setAlertMessage("")} />

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-300">Programas Z</h3>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Nuevo Programa Z
        </button>
      </div>

      {programas.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <Code2 className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Este proyecto todavía no tiene Programas Z propios.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {programas.map((p) => {
            const solMeta = solucionBadiMeta(p.solucion);
            return (
              <div key={p.id} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-white truncate">{p.nombre}</span>
                    {p.nombreTecnico && <span className="text-xs font-mono text-gray-400">{p.nombreTecnico}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-amber-300 bg-amber-500/10 border-amber-500/30">
                      Z
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-indigo-300 bg-indigo-500/10 border-indigo-500/30">
                      {solMeta.label}
                    </span>
                    {p.sistemasDisponibles && p.sistemasDisponibles.map((s) => (
                      <span key={s} className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border text-sky-300 bg-sky-500/10 border-sky-500/30">
                        {s}
                      </span>
                    ))}
                  </div>
                  {(p.badiRefIds.length > 0 || p.transaccionRefIds.length > 0) && (
                    <div className="mt-1.5 space-y-1">
                      {p.badiRefIds.length > 0 && (
                        <p className="text-[11px] text-gray-500 flex flex-wrap items-center gap-1">
                          <span className="text-gray-600">BAdIs:</span>
                          {p.badiRefIds.map((id) => (
                            <span key={id} className="text-purple-300 bg-purple-500/10 border border-purple-500/20 px-1.5 py-0.5 rounded-full">
                              {badiNombrePorId.get(id) || id}
                            </span>
                          ))}
                        </p>
                      )}
                      {p.transaccionRefIds.length > 0 && (
                        <p className="text-[11px] text-gray-500 flex flex-wrap items-center gap-1">
                          <span className="text-gray-600">Transacciones:</span>
                          {p.transaccionRefIds.map((id) => (
                            <span key={id} className="font-mono text-amber-300 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded-full">
                              {transaccionNombrePorId.get(id) || id}
                            </span>
                          ))}
                        </p>
                      )}
                    </div>
                  )}
                  {p.responsable && (
                    <p className="text-[11px] text-gray-500 mt-1.5">Responsable: <span className="text-gray-300">{p.responsable}</span></p>
                  )}
                </div>
                <button
                  onClick={() => setEditando(p)}
                  className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
                  title="Editar"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {esAdmin && (
                  <button
                    onClick={() => setAEliminar(p)}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all flex-shrink-0"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(modalOpen || editando) && (
        <ProgramaZFormModal
          projectId={projectId}
          programaInicial={editando}
          onGuardado={handleGuardado}
          onCancelar={handleCancelarModal}
        />
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar este Programa Z?"
          message={`"${aEliminar.nombre}" se eliminará de ${projectNombre || "este proyecto"}. Esta acción no se puede deshacer.`}
          confirmLabel={eliminando ? "Eliminando..." : "Eliminar"}
          danger
          onConfirm={handleConfirmarEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
